import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  FileText,
  ShieldCheck,
  Activity,
  TrendingUp,
  Wallet,
  Heart,
  AlertTriangle,
  Award
} from "lucide-react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendUp, color = "primary" }) => {
  const colorClasses = {
    primary: "bg-primary-50 text-primary",
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600"
  };

  return (
    <Card className="kpi-card animate-fadeIn" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className="font-heading font-bold text-2xl text-slate-900">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-xs ${trendUp ? 'text-emerald-600' : 'text-red-500'}`}>
              <TrendingUp className={`w-3 h-3 ${!trendUp && 'rotate-180'}`} />
              {trend}
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
};

// Lifecycle Funnel Data
const funnelData = [
  { stage: "Applied", count: 450, fill: "#F40d07" },
  { stage: "Verified", count: 380, fill: "#F59E0B" },
  { stage: "Trained", count: 320, fill: "#3B82F6" },
  { stage: "Active", count: 285, fill: "#10B981" },
];

// Performance Distribution
const performanceData = [
  { range: "0-60", count: 15 },
  { range: "60-70", count: 45 },
  { range: "70-80", count: 120 },
  { range: "80-90", count: 85 },
  { range: "90-100", count: 35 },
];

// Earnings Trend
const earningsTrend = [
  { month: "Jul", earnings: 2800000 },
  { month: "Aug", earnings: 3100000 },
  { month: "Sep", earnings: 3400000 },
  { month: "Oct", earnings: 3800000 },
  { month: "Nov", earnings: 4200000 },
  { month: "Dec", earnings: 4500000 },
];

// Tier Colors
const TIER_COLORS = {
  starter: "#94A3B8",
  pro: "#60A5FA",
  elite: "#F59E0B",
  master: "#1E293B"
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        
        const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const tierPieData = stats ? [
    { name: "Starter", value: stats.tier_distribution?.starter || 0, color: TIER_COLORS.starter },
    { name: "Pro", value: stats.tier_distribution?.pro || 0, color: TIER_COLORS.pro },
    { name: "Elite", value: stats.tier_distribution?.elite || 0, color: TIER_COLORS.elite },
    { name: "Master", value: stats.tier_distribution?.master || 0, color: TIER_COLORS.master },
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Therapists"
          value={stats?.total_therapists || 0}
          subtitle="Across all tiers"
          icon={Users}
          trend="+12% vs last month"
          trendUp={true}
          color="primary"
        />
        <KPICard
          title="New Applications"
          value={stats?.new_applications || 0}
          subtitle="This month"
          icon={FileText}
          trend="+8% vs last month"
          trendUp={true}
          color="blue"
        />
        <KPICard
          title="Verified"
          value={stats?.verified_count || 0}
          subtitle={`${stats?.pending_count || 0} pending`}
          icon={ShieldCheck}
          color="emerald"
        />
        <KPICard
          title="Active Therapists"
          value={stats?.active_therapists || 0}
          subtitle="Currently deployed"
          icon={Activity}
          trend="+5% vs last month"
          trendUp={true}
          color="amber"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Avg. Performance"
          value={`${stats?.avg_performance_score || 0}%`}
          icon={TrendingUp}
          trend="+2.3% improvement"
          trendUp={true}
          color="emerald"
        />
        <KPICard
          title="Avg. Monthly Earnings"
          value={`RM ${(stats?.avg_earnings || 0).toLocaleString()}`}
          icon={Wallet}
          color="blue"
        />
        <KPICard
          title="Retention Rate"
          value={`${stats?.retention_rate || 0}%`}
          icon={Heart}
          color="primary"
        />
        <KPICard
          title="Churn Risk"
          value={stats?.churn_risk_count || 0}
          subtitle="Therapists at risk"
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lifecycle Funnel */}
        <Card className="chart-container" data-testid="lifecycle-funnel-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Therapist Lifecycle Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" />
                <YAxis type="category" dataKey="stage" width={80} />
                <Tooltip 
                  formatter={(value) => [value, 'Count']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {funnelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tier Distribution */}
        <Card className="chart-container" data-testid="tier-distribution-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Tier Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={tierPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {tierPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [value, name]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              {tierPieData.map((tier) => (
                <div key={tier.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color }}></div>
                  <span className="text-sm text-slate-600">{tier.name}</span>
                  <Badge variant="secondary" className="text-xs">{tier.value}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Distribution */}
        <Card className="chart-container" data-testid="performance-distribution-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Performance Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, 'Therapists']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="count" fill="#F40d07" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Earnings Trend */}
        <Card className="chart-container" data-testid="earnings-trend-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Platform Earnings Trend (RM)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={earningsTrend}>
                <defs>
                  <linearGradient id="earningsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F40d07" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#F40d07" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                <Tooltip 
                  formatter={(value) => [`RM ${value.toLocaleString()}`, 'Earnings']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="earnings" 
                  stroke="#F40d07" 
                  strokeWidth={2}
                  fill="url(#earningsGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* LMS Completion */}
      <Card className="chart-container" data-testid="lms-completion-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            LMS Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-3xl font-heading font-bold text-slate-900">{stats?.total_therapists || 0}</p>
              <p className="text-sm text-slate-500">Enrolled</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <p className="text-3xl font-heading font-bold text-emerald-600">{stats?.avg_lms_completion || 0}%</p>
              <p className="text-sm text-slate-500">Avg. Completion</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-heading font-bold text-red-600">12</p>
              <p className="text-sm text-slate-500">Failed Exams</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-3xl font-heading font-bold text-amber-600">8</p>
              <p className="text-sm text-slate-500">Certs Expiring Soon</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
